import React from 'react';
import { Link } from 'react-router-dom';
import { Truck, Package, Clock, MapPin, Phone, CheckCircle } from 'lucide-react';

export default function ShippingPolicyPage() {
  return (
    <div className="min-h-screen bg-white">
      <div className="bg-gray-900 text-white py-14 px-4 text-center">
        <p className="text-xs tracking-[0.3em] text-blue-400 uppercase mb-3">BabyCub Policies</p>
        <h1 className="text-3xl md:text-4xl font-black">Shipping Policy</h1>
        <p className="text-gray-400 text-sm mt-3">Fast & reliable delivery of baby wear across Pakistan</p>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14">

        {/* Shipping Cost Banner */}
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-7 text-white text-center mb-12 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-40 h-40 bg-red-600/10 rounded-full -translate-y-10 translate-x-10" />
          <Truck size={36} className="mx-auto mb-4 text-red-400" />
          <h2 className="text-xl font-black uppercase tracking-widest mb-4">Delivery Charges</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-white/10 rounded-xl p-5 border border-white/10">
              <div className="text-2xl font-black text-green-400 mb-1">FREE</div>
              <div className="text-sm text-gray-300 font-semibold">On orders above</div>
              <div className="text-xl font-black text-white mt-1">Rs. 10,000</div>
            </div>
            <div className="bg-white/10 rounded-xl p-5 border border-white/10">
              <div className="text-2xl font-black text-yellow-400 mb-1">Rs. 250</div>
              <div className="text-sm text-gray-300 font-semibold">On orders below</div>
              <div className="text-xl font-black text-white mt-1">Rs. 10,000</div>
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-4">* Shipping charges are calculated at checkout</p>
        </div>

        {/* Key Info Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-12">
          {[
            { icon: Clock, title: 'Processing Time', desc: 'Orders are processed within 1–2 business days after payment confirmation.' },
            { icon: Package, title: 'Delivery Time', desc: '3–5 business days for most cities. Remote areas may take 5–7 business days.' },
            { icon: MapPin, title: 'Coverage', desc: 'We deliver to all major cities and towns across Pakistan via our logistics partners.' },
            { icon: CheckCircle, title: 'Order Tracking', desc: 'You will receive a tracking number via SMS/email once your order is dispatched.' },
          ].map(item => (
            <div key={item.title} className="flex items-start gap-4 bg-gray-50 rounded-xl p-5 border border-gray-100">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <item.icon size={18} className="text-blue-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-sm mb-1">{item.title}</h3>
                <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Detailed Sections */}
        <div className="space-y-8">
          <div>
            <h2 className="text-base font-bold text-gray-900 mb-3 uppercase tracking-wide">1. Shipping Rates</h2>
            <div className="overflow-hidden rounded-xl border border-gray-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-900 text-white">
                    <th className="px-5 py-3 text-left font-semibold tracking-wide">Order Total</th>
                    <th className="px-5 py-3 text-left font-semibold tracking-wide">Shipping Charge</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-gray-100">
                    <td className="px-5 py-3.5 text-gray-600">Below Rs. 10,000</td>
                    <td className="px-5 py-3.5 font-semibold text-gray-900">Rs. 250</td>
                  </tr>
                  <tr className="border-t border-gray-100 bg-green-50">
                    <td className="px-5 py-3.5 text-gray-600">Rs. 10,000 and above</td>
                    <td className="px-5 py-3.5 font-bold text-green-600">FREE</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h2 className="text-base font-bold text-gray-900 mb-3 uppercase tracking-wide">2. Delivery Timeframes</h2>
            <div className="overflow-hidden rounded-xl border border-gray-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-900 text-white">
                    <th className="px-5 py-3 text-left font-semibold">Region</th>
                    <th className="px-5 py-3 text-left font-semibold">Estimated Time</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { region: 'Lahore (City)', time: '1–2 Business Days' },
                    { region: 'Karachi, Islamabad, Rawalpindi', time: '2–3 Business Days' },
                    { region: 'Other Major Cities', time: '3–5 Business Days' },
                    { region: 'Remote / Rural Areas', time: '5–7 Business Days' },
                  ].map((row, i) => (
                    <tr key={row.region} className={`border-t border-gray-100 ${i % 2 === 1 ? 'bg-gray-50' : ''}`}>
                      <td className="px-5 py-3.5 text-gray-600">{row.region}</td>
                      <td className="px-5 py-3.5 font-medium text-gray-900">{row.time}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-400 mt-2">* Delivery times are estimates and may vary during peak seasons or public holidays.</p>
          </div>

          <div>
            <h2 className="text-base font-bold text-gray-900 mb-3 uppercase tracking-wide">3. Cash on Delivery (COD)</h2>
            <ul className="space-y-2">
              {[
                'Cash on Delivery is available for all orders across Pakistan.',
                'Please have the exact amount ready at the time of delivery.',
                'COD orders may take 1 additional business day to process.',
              ].map(item => (
                <li key={item} className="flex items-start gap-2 text-sm text-gray-600 leading-relaxed">
                  <span className="text-blue-500 mt-1 flex-shrink-0">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="text-base font-bold text-gray-900 mb-3 uppercase tracking-wide">4. Order Tracking</h2>
            <p className="text-sm text-gray-600 leading-relaxed mb-3">
              Once your order is dispatched, you will receive an SMS and email with your tracking number. You can use this to track your package on our logistics partner's website.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              For any delivery issues, please contact our support team and we'll resolve it promptly.
            </p>
          </div>

          <div>
            <h2 className="text-base font-bold text-gray-900 mb-3 uppercase tracking-wide">5. Failed Delivery</h2>
            <p className="text-sm text-gray-600 leading-relaxed">
              If a delivery attempt fails (customer unavailable, wrong address), our courier will try up to 2 more times. After 3 failed attempts, the order will be returned to us. Reshipping fees will apply for re-delivery.
            </p>
          </div>
        </div>

        <div className="mt-12 bg-blue-50 rounded-xl p-6 border border-blue-100 flex flex-col sm:flex-row items-center gap-4">
          <Phone size={28} className="text-blue-600 flex-shrink-0" />
          <div className="text-center sm:text-left flex-1">
            <p className="font-bold text-gray-900 text-sm">Need shipping help?</p>
            <p className="text-xs text-gray-500 mt-0.5">Call / WhatsApp BabyCub at 03363898930</p>
          </div>
          <Link to="/contact" className="bg-blue-600 text-white px-5 py-2.5 text-xs font-bold uppercase tracking-widest rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap">
            Contact Us
          </Link>
        </div>
      </div>
    </div>
  );
}
